import {
  Sparkles,
  Zap,
  Bell,
  Wand2,
  LayoutDashboard,
  LineChart,
  Eye,
  MessageSquareText,
  Hand,
  Radar,
} from "lucide-react";
import { GeometricBadge } from "../playful/GeometricBadge";
import { FloatingIcon } from "../playful/FloatingIcon";
import { Reveal } from "./Reveal";

// The shipped AI layer — grounded on your real click data and, crucially, ACTING
// on it. Order leads with the differentiators (acts + reaches out), then explains.
const ASSISTS = [
  {
    icon: Zap,
    tone: "candy",
    title: "One-click fixes",
    desc: "AI doesn’t just recommend — it does the work. Turn on conversion tracking, tag your traffic, seed a routing rule, all in one tap. Every action has one-click undo.",
    chips: ["apply", "undo", "done ✓"],
  },
  {
    icon: Bell,
    tone: "pink",
    title: "Proactive alerts",
    desc: "It watches every link and pings you first — a link expiring soon, one near its click cap, a winner with no tracking. Value before you even ask.",
    chips: ["expiring soon", "near cap", "untracked winner"],
  },
  {
    icon: Wand2,
    tone: "mint",
    title: "AI Strategist",
    desc: "Describe a goal — “grow newsletter signups” — and get a grounded, prioritized plan with the numbers behind it. Apply the whole plan in one batch.",
    chips: ["goal → plan", "grounded", "apply all"],
  },
  {
    icon: LayoutDashboard,
    tone: "candy",
    title: "AI Briefing",
    desc: "A stand-up every time you open the dashboard: what changed since your last visit, why it changed, and your single best next move.",
    chips: ["what changed", "why", "do next"],
  },
  {
    icon: LineChart,
    tone: "pink",
    title: "Analytics Intelligence",
    desc: "Every chart explained in plain English — trends, statistical anomalies, and a 30-day forecast. Computed from your real numbers, never invented.",
    chips: ["explain this", "anomalies", "30-day forecast"],
  },
  {
    icon: Sparkles,
    tone: "mint",
    title: "Built from a sentence",
    desc: "Describe what you’re sharing and get the alias, UTMs, tags and campaign built for you — or a full link-in-bio page, copy and theme included.",
    chips: ["/summer-sale", "utm=instagram", "bio page"],
  },
];

// The loop, stated plainly: most "AI" stops at the first two.
const LOOP = [
  { icon: Eye, label: "Understands" },
  { icon: MessageSquareText, label: "Explains" },
  { icon: Hand, label: "Acts" },
  { icon: Radar, label: "Reaches out" },
];

const shadow = {
  candy: "shadow-sticker-candy",
  pink: "shadow-sticker-pink",
  mint: "shadow-sticker-mint",
};

/** AISection — AI as a grounded operator that acts, not a chatbot that answers. */
export default function AISection() {
  return (
    <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10" data-testid="ai-section">
      <Reveal className="mx-auto mb-10 max-w-2xl text-center">
        <GeometricBadge tone="mint">
          <Sparkles size={11} strokeWidth={3} /> AI-NATIVE
        </GeometricBadge>
        <h2 className="mt-5 font-display text-4xl font-extrabold leading-[0.95] tracking-tight md:text-6xl">
          Not a chatbot.
          <span className="ai-gradient-text"> An operator that acts.</span>
        </h2>
        <p className="mt-4 font-sans text-lg leading-relaxed text-muted-foreground">
          LinkLane reads your own click data, tells you what it means, then does something about it
          — applies the fix, flags what needs you, and plans your next move. Right where you work.
          No prompt to babysit.
        </p>
      </Reveal>

      {/* The loop — Understands · Explains · Acts · Reaches out */}
      <Reveal className="mb-12 flex flex-wrap items-center justify-center gap-2 md:gap-3">
        {LOOP.map((step, i) => (
          <div key={step.label} className="flex items-center gap-2 md:gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-ink bg-white px-3.5 py-2 font-display text-sm font-bold shadow-sticker-xs">
              <step.icon size={15} strokeWidth={2.5} className="text-candy" />
              {step.label}
            </span>
            {i < LOOP.length - 1 ? (
              <span className="font-mono text-muted-foreground" aria-hidden>
                →
              </span>
            ) : null}
          </div>
        ))}
      </Reveal>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {ASSISTS.map((a, i) => (
          <Reveal key={a.title} delay={i * 80}>
            <div
              className={`h-full border-2 border-ink ${shadow[a.tone]} sticker-hover flex flex-col gap-4 rounded-3xl bg-white p-7`}
            >
              <FloatingIcon icon={a.icon} tone={a.tone} size={48} shape="square" />
              <div>
                <h3 className="font-display text-xl font-extrabold leading-tight">{a.title}</h3>
                <p className="mt-2 font-sans text-sm leading-relaxed text-muted-foreground">
                  {a.desc}
                </p>
              </div>
              <div className="mt-auto flex flex-wrap gap-1.5 pt-2">
                {a.chips.map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full border border-ink/30 bg-paper px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={300} className="mt-6 text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          Included on every paid plan · bring your own AI key on Enterprise · grounded, never
          invented
        </p>
      </Reveal>
    </section>
  );
}
